import React from 'react';
import DocumentPage, {
  Section,
  Paragraph,
  HazardTable,
} from './DocumentPage';

const DrivingGoodsVehiclesDoc = ({ onReturnHome }) => (
  <DocumentPage
    pageTitle="Driving Goods Vehicles – Risk Assessment"
    documentHeading="Risk Assessment 10: Driving Goods Vehicles"
    meta={[
      ['Document Reference', 'PVS-RA-10'],
      ['Version', '1.0'],
      ['Date of Issue', '17th March 2026'],
      ['Next Review Date', '17th March 2028'],
      ['Area / Location', 'On and off site'],
      ['Activity', 'Driving goods vehicles'],
      ['Review Period', '24 months'],
    ]}
    acknowledgment={{
      documentName: 'Driving Goods Vehicles – Risk Assessment',
      documentReference: 'PVS-RA-10',
      processActivity: 'Driving Goods Vehicles',
      versionNumber: '1.0',
      dateOfIssue: '17th March 2026',
    }}
    onReturnHome={onReturnHome}
  >
    <Section number="1" title="Scope">
      <Paragraph>
        This risk assessment applies to employees, volunteers and contractors driving goods vehicles
        on behalf of Pressure Vessel Services Limited, both on site and on the public highway.
      </Paragraph>
    </Section>

    <Section number="2" title="Hazard Identification and Risk Assessment">
      <HazardTable
        columns={['Hazard', 'Control Measures Already in Place', 'Additional Control Measures Required']}
        rows={[
          [
            'General Controls',
            [
              'Seat belts are worn by drivers and passengers.',
              'Vehicles are maintained and inspected.',
              'Vehicle defect checks completed before use.',
              'Drivers are provided with relevant training and information.',
            ],
            'None required.',
          ],
          [
            'Injury to passengers',
            [
              'Only authorised passengers are permitted within the vehicle.',
              'Passengers are instructed to remain in the vehicle unless instructed otherwise and must follow the instructions of the driver.',
            ],
            'None required.',
          ],
          [
            'Speeding',
            [
              'Drivers are instructed to adhere to the requirements of the Road Traffic Act / Highway Code.',
              'Drivers are instructed not to exceed designated speed limits.',
              'Vehicles are fitted with speed limiters where required.',
              'All goods vehicles with a gross vehicle weight over 3.5 tonnes are fitted with approved tachographs.',
            ],
            'None required.',
          ],
          [
            'Lack of concentration (mobile phones, sat-navs, eating / drinking / smoking, entertainment systems)',
            [
              'Hand-held mobile phones are not to be used while the vehicle is in motion or stationary at traffic lights etc.',
              'Sat Nav systems are to be set prior to moving the vehicle.',
              'Drivers are instructed not to smoke in the vehicles as per legislation.',
            ],
            'None required.',
          ],
          [
            'Lack of experience',
            ['Only suitably experienced and licensed persons are authorised to drive Company vehicles.'],
            'None required.',
          ],
          [
            'Driver fatigue / falling asleep at the wheel',
            [
              'Drivers are encouraged to take regular breaks, especially if travelling long distances.',
              'All goods vehicles with a gross vehicle weight over 3.5 tonnes are fitted with approved tachographs.',
              'All drivers complete tachographs as required, take their required breaks and records are maintained.',
            ],
            'None required.',
          ],
          [
            'Stress',
            ['Routes / schedules are planned to allow drivers to take regular breaks, especially if travelling long distances.'],
            'None required.',
          ],
          [
            'Alcohol and drugs',
            [
              'Drivers are instructed to adhere to the requirements of the Road Traffic Act.',
              'Drug and Alcohol screening is carried out.',
              'Drivers are instructed to inform management of any prescription drugs and not to operate vehicles whilst prescribed drugs may prevent safe operation.',
            ],
            'None required.',
          ],
          [
            'Personal safety (lone working, accidents, breakdowns, road rage)',
            [
              'Drivers carry mobile phones to summon help / the police in the event of an emergency.',
              'Out of hours contact details provided.',
              'Employees have been instructed in the procedure to be followed should the vehicle break down.',
              'Drivers are instructed of the procedure to follow in the event of an accident.',
              'Drivers are instructed to adhere to the requirements of the Road Traffic Act.',
            ],
            'None required.',
          ],
          [
            'Collision with person, other vehicles, buildings etc',
            [
              'Vehicles are fitted with audible reversing alarms.',
              'Vehicles are fitted with external side-mounted and rear-view mirrors. These are kept clean and in good repair.',
            ],
            'None required.',
          ],
          [
            'Bad weather',
            ['Drivers are instructed to adjust their driving style to take into account the weather and road conditions.'],
            'None required.',
          ],
          [
            'Use of un-roadworthy vehicle / Safety critical mechanical failure / Unsuitable vehicle',
            [
              "All goods vehicles are subject to planned preventative maintenance / servicing on a regular basis in accordance with the Company's Operating Licence.",
              'Suitability of vehicles to contract is considered by Management before the vehicle is assigned.',
            ],
            'Introduce documented daily (or pre-use) safety check sheets for each wagon.',
          ],
          [
            'Insecure or otherwise unsafe load',
            [
              'Drivers have been instructed to check the securing of the load they are transporting.',
              'Drivers visually check the condition of any restraining devices being used prior to moving off.',
              'Drivers have been informed of the maximum weight of load permitted for the vehicle they are driving.',
              'Drivers have been informed of individual permitted axle weights.',
              'Drivers are responsible for ensuring that the weight of the load does not exceed the maximum permissible axle weights.',
              'Drivers are instructed on the action to be taken in the event of an emergency.',
            ],
            'None required.',
          ],
          [
            'Personal Protective Equipment',
            [
              'Drivers are provided with suitable PPE including: steel toe cap footwear with good grip; hi-visibility jacket / coat; rigger gloves; hard hat; wet weather (hi-visibility) clothing.',
            ],
            'None required.',
          ],
        ]}
      />
    </Section>

    <Section number="3" title="Reminder">
      <Paragraph className="font-semibold">
        You have a duty not only to protect your own health and safety but also to ensure that your
        actions do not put others around you at risk.
      </Paragraph>
    </Section>
  </DocumentPage>
);

export default DrivingGoodsVehiclesDoc;
